import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Chip, ClickAwayListener, IconButton, Popper, TextField, Typography } from '@mui/material';
import styles from 'pages/edit-task/EditTask.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutesType } from 'shared/types/routes/routers-type';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useForm } from 'shared/utils/hooks/useForm';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { DirectoryLastFetch, DirectoryThemes } from 'entities/directory/selector';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import { format, parse } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import AllContractsList from 'widgets/all-contracts-list/AllContractsList';
import imageCompression from 'browser-image-compression';
import CustomButton from 'shared/custom-button/CustomButton';
import { IFile } from 'entities/create-task/types/CreateTask-interface';
import { fetchCreateTask } from 'entities/create-task/actions/fetch-create-task';
import { CreateTaskError, CreateTaskID, CreateTaskLoading, CreateTaskNumber } from 'entities/create-task/selector';
import { resetCreateTask } from 'entities/create-task';
import Answer from 'features/create-task/ui/Answer';
import * as Yup from 'yup';
import { DateCalendar } from '@mui/x-date-pickers';
import Loader from 'shared/loader/Loader';
import { fetchDirectory } from 'entities/directory/actions/fetch-directory';
import appStyles from 'app/providers/theme/App.module.css';
import { fetchFileDownload } from 'entities/task/actions/fetch-task-case';

import { Download, FileData, FileName, LastFetchTask, LoadingTask, TaskCase, TaskID } from 'entities/task/selector';
import { resetFileData } from 'entities/task';
import { validationSchema } from 'features/edit-task/utils/validationSchema';
import { fetchEditTask } from 'entities/edit-task/actions/fetch-edit-task';
import { EditTaskID, EditTaskLoading, EditTaskNumber } from 'entities/edit-task/selector';
import { resetEditTask } from 'entities/edit-task';

type FileItem = {
    Name: string;
    DataBase64: string;
};

type FileTaskItem = {
    FromHR: boolean;
    ID: string;
    Name: string;
}

export const EditTask: FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [fileError, setFileError] = useState<string | null>(null);
    const [isCompressing, setIsCompressing] = useState(false);
    const [open, setOpen] = useState(false);

    const themesList = useAppSelector(DirectoryThemes);

    const isLoading = useAppSelector(EditTaskLoading);
    const editTaskId = useAppSelector(EditTaskID);
    const editTaskNumber = useAppSelector(EditTaskNumber);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const lastFetch = useAppSelector(DirectoryLastFetch);

    useEffect(() => {
        const currentTime = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;
        const isDataStale = !lastFetch || (currentTime - lastFetch) > oneDay;
        if (isDataStale) {
            dispatch(fetchDirectory());
        }
    }, [dispatch, lastFetch]);

    const taskInfo = location.state?.taskInfo;

    const handleClickBack = () => {
        navigate(`/tasks/case/?id=${taskInfo.ID}`);
    };
    const parseDateString = (dateString: string): Date | null => {
        if (!dateString) return null;
        try {
            return parse(dateString, 'dd.MM.yyyy', new Date());
        } catch (error) {
            console.error('Ошибка парсинга даты:', error);
            return null;
        }
    };

    const handleDatePickerChange = (newValue: Date | null) => {
        const dateString = newValue ? format(newValue, 'dd.MM.yyyy') : '';
        setFormData((prev: any) => ({
            ...prev,
            Date: dateString,
        }));
        setOpen(false);
    };

    const userData = {
        Themes: (themesList.find((theme) => theme.Name === taskInfo.ThemeName))?.ID,
        Comment: taskInfo.AuthorsComment,
        Date: taskInfo.AuthorsExpectedCompletionDate || '',
        Files: taskInfo.Files,
        FilesAdd: []
    };

    const {
        formData,
        handleChangeInput,
        errors,
        setErrors,
        handleAutocompeteChangeArray,
        handleAutocompeteChange,
        handleRemoveFile,
        handleRemoveFilesAdd,
        setFormData
    } = useForm(userData, validationSchema);

    const selectedTheme = useMemo(() =>
        themesList.find(theme => theme.ID === formData.Themes) || null,
        [themesList, formData.Themes]
    );

    const textFieldRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (textFieldRef.current) {
            textFieldRef.current.style.height = 'auto';
            textFieldRef.current.style.height = `${textFieldRef.current.scrollHeight}px`;
        }
    }, [formData.Comment]);

    const handleClickAway = () => {
        setOpen(false);
    };

    const acceptedMimeTypes = [
        'image/jpeg', 'image/pjpeg', 'image/jpg',
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroenabled.12',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/x-zip-compressed',
        'application/x-rar-compressed'
    ];

    // Функция преобразования расширения в MIME-тип
    const convertExtToMime = (ext: string) => {
        switch (ext.toLowerCase()) {
            case 'jpg':
            case 'jpeg': return 'image/jpeg';
            case 'pdf': return 'application/pdf';
            case 'xls': return 'application/vnd.ms-excel';
            case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            case 'xlsm': return 'application/vnd.ms-excel.sheet.macroenabled.12';
            case 'doc': return 'application/msword';
            case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            case 'zip': return 'application/x-zip-compressed';
            case 'rar': return 'application/x-rar-compressed';
            default: return ext;
        }
    };

    const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1]);
            };
            reader.onerror = error => reject(error);
            reader.readAsDataURL(file);
        });
    };

    // Обработчик загрузки файлов с проверками
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const newFiles = input.files;
        if (!newFiles || newFiles.length === 0) return;

        setFileError(null);
        setIsCompressing(true);

        const MAX_FILE_SIZE_MB = 20; // Максимальный размер файла
        const MAX_TOTAL_FILES = 20; // Максимальное количество файлов
        const selectedFiles = Array.from(newFiles);

        // Проверка количества файлов
        if (formData.FilesAdd.length + selectedFiles.length > MAX_TOTAL_FILES) {
            setFileError(`Максимальное количество файлов: ${MAX_TOTAL_FILES}`);
            setIsCompressing(false);
            return;
        }

        // Проверка типов файлов
        const invalidFiles = selectedFiles.filter(file => {
            const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
            const mimeType = convertExtToMime(fileExt);
            return !acceptedMimeTypes.includes(mimeType);
        });

        if (invalidFiles.length > 0) {
            setFileError(`Недопустимый формат: ${invalidFiles[0].name}`);
            setIsCompressing(false);
            return;
        }

        // Проверка размера файлов
        const oversizedFiles = selectedFiles.filter(file =>
            file.size > MAX_FILE_SIZE_MB * 1024 * 1024
        );

        if (oversizedFiles.length > 0) {
            setFileError(
                `Файл слишком большой: ${oversizedFiles[0].name} (макс. ${MAX_FILE_SIZE_MB} МБ)`
            );
            setIsCompressing(false);
            return;
        }

        try {
            // Разделяем файлы на изображения и остальные
            const imageFiles = selectedFiles.filter(file =>
                ['image/jpeg', 'image/pjpeg', 'image/jpg'].includes(file.type)
            );
            const otherFiles = selectedFiles.filter(file =>
                !['image/jpeg', 'image/pjpeg', 'image/jpg'].includes(file.type)
            );

            // Сжимаем изображения
            const compressedFiles = await Promise.all(
                imageFiles.map(async file => {
                    try {
                        const options = {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1024,
                            useWebWorker: true,
                        };
                        return await imageCompression(file, options);
                    } catch (error) {
                        console.error('Ошибка сжатия:', error);
                        return file; // Возвращаем оригинал при ошибке
                    }
                })
            );

            const allFiles = [...compressedFiles, ...otherFiles];

            const newFileItems: IFile[] = [];
            for (const file of allFiles) {
                const base64Data = await convertFileToBase64(file);
                newFileItems.push({
                    Name: file.name,
                    DataBase64: base64Data
                });
            }

            setFormData((prev: any) => ({
                ...prev,
                FilesAdd: [...(prev.FilesAdd || []), ...newFileItems]
            }));

        } catch (error) {
            console.error('Ошибка обработки файлов:', error);
            setFileError('Ошибка при обработке файлов');
        } finally {
            setIsCompressing(false);
            input.value = '';
        }
    };

    const validateForm = async (formData: any) => {
        try {
            await validationSchema.validate(formData, { abortEarly: false });
            return null;
        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                const errors: { [key: string]: string } = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        errors[error.path] = error.message;
                    }
                });
                return errors; 
            }
            return null;
        }
    };

    const [saveAsDraft, setSaveAsDraft] = useState(false);
    const handleSend = async () => {
        const validationErrors = await validateForm(formData);
        if (validationErrors) {
            setErrors(validationErrors);
            return;
        }
        dispatch(fetchEditTask({TaskID: taskInfo.ID, formData: formData, saveAsDraft: false, deletedFileIds: deletedFileIds }));
        setSaveAsDraft(false);
    };

    const handleSave = async () => {
        const validationErrors = await validateForm(formData);
        if (validationErrors) {
            setErrors(validationErrors);
            return;
        }
        dispatch(fetchEditTask({TaskID: taskInfo.ID, formData: formData, saveAsDraft: true, deletedFileIds: deletedFileIds }));
        setSaveAsDraft(true);
    };

    useEffect(() => {
        return () => {
            dispatch(resetEditTask());
        };
    }, []);

    const fileData = useAppSelector(FileData);
    const fileName = useAppSelector(FileName);
    const isDownload = useAppSelector(Download);

    useEffect(() => {
        if (fileData) {
            const link = document.createElement('a');
            link.href = `data:application/octet-stream;base64,${fileData}`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            dispatch(resetFileData());
        }
    }, [fileData, isDownload, fileName, dispatch]);

    const handleDownload = async (fileId: string) => {
        if (taskInfo.ID) {
            try {
                await dispatch(fetchFileDownload({
                    TaskID: taskInfo.ID,
                    FileID: fileId
                }))

            } catch (error) {
                console.error('Error downloading the file:', error);
            }
        }

    };
    const truncateFileName = (name: string, maxLength: number = 16): string => {
        if (name.length <= maxLength) {
            return name;
        }
        return `${name.substring(0, maxLength)}...`;
    };

    const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]);
    const onRemoveFile = (fileId: string) => {
        setDeletedFileIds((prev) => [...prev, fileId]);
    };

    if (editTaskId && editTaskNumber) {
        return (
            <Answer
                number={editTaskNumber}
                id={editTaskId}
                idContract={location.state?.id ? location.state.id : null}
                saveAsDraft={saveAsDraft}
            />
        )
    } else {
        return (
            <Box>
                <Box className={appStyles.titleBox}>
                    <h1>Редактирование заявки <span>{taskInfo.Number}</span></h1>
                    <Box className={styles.titleBtn}>
                        <Button onClick={handleClickBack} className={styles.btnBack}><KeyboardBackspaceIcon /> Назад к заявке</Button>
                    </Box>
                </Box>
                {isLoading && (
                    <Loader
                        title="Сохранение заявки..."
                    />
                )}
                {!isLoading && (
                    <Box className={styles.formBox}>
                        <Box className={styles.formContent}>
                            <Box className={styles.selectsBox}>
                                <Autocomplete
                                    options={themesList}
                                    getOptionLabel={(option) => option.Name || ''}
                                    value={selectedTheme}
                                    onChange={(event, newValue) => {
                                        if (newValue && !themesList.some(t => t.ID === newValue.ID)) {
                                            handleAutocompeteChangeArray('', 'Themes');
                                        } else {
                                            handleAutocompeteChangeArray(newValue?.ID || '', 'Themes');
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Выберите тему"
                                            name="Themes"
                                            variant="outlined"
                                            error={!!errors.Themes}
                                            helperText={errors.Themes || ''}
                                        />
                                    )}
                                />

                                <Button
                                    ref={buttonRef}
                                    variant="outlined"
                                    onClick={() => setOpen(!open)}
                                    fullWidth
                                    sx={{
                                        height: '40px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        textTransform: 'none',
                                        borderColor: errors.Date ? '#f44336' : undefined
                                    }}
                                >
                                    <span style={{ color: formData.Date ? 'inherit' : '#b0b0b0' }}>
                                        {formData.Date ? formData.Date : 'Выберите желаемую дату выполнения'}
                                    </span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                                    </svg>
                                </Button>
                                {errors.Date && (
                                    <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {errors.Date}
                                    </Typography>
                                )}
                            </Box>

                            <Popper
                                open={open}
                                anchorEl={buttonRef.current}
                                placement="bottom-start"
                                style={{ zIndex: 1300 }}
                            >
                                <ClickAwayListener onClickAway={handleClickAway}>
                                    <Box
                                        sx={{
                                            bgcolor: 'background.paper',
                                            boxShadow: 3,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0'
                                        }}
                                    >
                                        <LocalizationProvider
                                            dateAdapter={AdapterDateFns}
                                            adapterLocale={ru}
                                        >
                                            <DateCalendar
                                                value={parseDateString(formData.Date)}
                                                onChange={handleDatePickerChange}
                                                minDate={new Date()}
                                                disableHighlightToday
                                                showDaysOutsideCurrentMonth
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                </ClickAwayListener>
                            </Popper>
                            <Box className={styles.dopBox}>
                                <TextField
                                    inputRef={textFieldRef}
                                    label="Оставьте комментарий к заявке"
                                    multiline
                                    rows={1}
                                    value={formData.Comment}
                                    name="Comment"
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeInput({ event })}
                                    variant="outlined"
                                    error={!!errors.Comment}
                                    helperText={errors.Comment || ''}
                                    sx={{
                                        width: '500px',
                                        '& .MuiInputBase-root': {
                                            height: 'auto',
                                            alignItems: 'flex-start',
                                            minHeight: '110px'
                                        },
                                    }}
                                />

                            </Box>
                            {taskInfo.Files.length > 0 && (
                                <Box className={styles.filesBox}>
                                    <h4>Файлы заявки</h4>
                                    {taskInfo.Files
                                        .filter((file: FileTaskItem) => !deletedFileIds.includes(file.ID))
                                        .map((file: FileTaskItem) => (
                                            <Chip
                                                key={file.ID}
                                                label={
                                                    <a
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleDownload(file.ID);
                                                        }}
                                                        style={{ color: 'inherit', textDecoration: 'none' }}
                                                    >
                                                        {truncateFileName(file.Name)}
                                                    </a>
                                                }
                                                onDelete={() => onRemoveFile(file.ID)}
                                                deleteIcon={<DeleteIcon />}
                                                variant="outlined"
                                                sx={{ margin: '2px' }}
                                            />
                                        ))}
                                </Box>
                            )}
                            <Box mt={2}>
                                <input
                                    accept={acceptedMimeTypes.join(',')}
                                    style={{ display: 'none' }}
                                    id="file-upload"
                                    type="file"
                                    multiple
                                    onChange={handleFileChange}
                                    disabled={isCompressing}
                                />
                                <label htmlFor="file-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        disabled={isCompressing}
                                    >
                                        {isCompressing ? 'Обработка...' : 'Добавить файл'}
                                    </Button>
                                </label>

                                {fileError && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {fileError}
                                    </Typography>
                                )}

                                <Box mt={1}>
                                    {formData.FilesAdd?.map((file: FileItem, index: number) => (
                                        <Chip
                                            key={index}
                                            label={file.Name}
                                            onDelete={() => handleRemoveFilesAdd(index)}
                                            deleteIcon={<DeleteIcon />}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}


                {(!isLoading && !editTaskId && !editTaskNumber) && (
                    <Box className={styles.actionsBtnBox}>
                        <CustomButton
                            title="Отправить"
                            handleClick={handleSend}
                            textColor="white"
                            backgroundColor="#0054E6"
                            gap="10px"
                            border="none"
                            borderRadius="4px"
                            padding="8px 30px"
                            disabled={isLoading}
                        />
                        <CustomButton
                            title="Сохранить как черновик"
                            handleClick={handleSave}
                            textColor="#0054E6"
                            backgroundColor="none"
                            gap="10px"
                            border="none"
                            borderRadius="4px"
                            padding="8px 30px"
                            disabled={isLoading}
                        />
                    </Box>
                )}

            </Box>
        );
    }
};

export default EditTask;